import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, BookOpen, BarChart3, PieChart, Settings, LogOut, TrendingUp, Plus, Users, Bell, ChevronsLeft, ChevronsRight, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAutoReloadOnNewBuild } from "@/lib/useAutoReloadOnNewBuild";
import NewTradeSheet from "@/components/NewTradeSheet";
import { canCreateTrade, canManageUsers, canManageNotificationTemplates, canSeeDollarStats, ROLE_LABELS, ROLES } from "@/lib/permissions";

const SIDEBAR_KEY = "zzx.sidebar.collapsed";

const baseNav = [
  { to: "/journal", label: "Journal", icon: BookOpen, testId: "nav-journal" },
  { to: "/global-stats", label: "Global Stats", icon: PieChart, testId: "nav-global-stats" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

const ROLE_TONE = {
  [ROLES.SUPER_ADMIN]: "text-amber-400",
  [ROLES.ADMIN]: "text-blue-400",
  [ROLES.CONTRIBUTOR]: "text-emerald-400",
  [ROLES.GENERAL]: "text-zinc-500",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  // Auto-detect new deploys and hard-reload the tab so users always run the
  // latest code — no Ctrl+Shift+R needed. No-op in preview / dev.
  useAutoReloadOnNewBuild();
  const [collapsed, setCollapsed] = useState(() => {
    // Cache last-known state locally so the sidebar doesn't flash on reload
    // before the user profile hydrates.
    try {
      const raw = localStorage.getItem(SIDEBAR_KEY);
      if (raw === "1") return true;
      if (raw === "0") return false;
    } catch { /* ignore */ }
    // New browser and unknown user → default to minimized per spec.
    return true;
  });
  const nav_go = useNavigate();

  // Hydrate from the server-side per-user preference once the full profile
  // arrives. Waits for the `sidebar_collapsed` key to be present so we don't
  // clobber it with the local cache value.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !user || user === false) return;
    if (!("sidebar_collapsed" in user)) return;
    if (user.sidebar_collapsed === true || user.sidebar_collapsed === false) {
      setCollapsed(user.sidebar_collapsed);
      try { localStorage.setItem(SIDEBAR_KEY, user.sidebar_collapsed ? "1" : "0"); } catch { /* ignore */ }
    } else {
      // Brand-new user: persist the "minimized by default" spec so it sticks
      // across their next login.
      api.patch("/auth/me", { sidebar_collapsed: true }).catch(() => {});
    }
    hydratedRef.current = true;
  }, [user]);

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      // Persist per-user; ignore failures (offline / logged out).
      api.patch("/auth/me", { sidebar_collapsed: next }).catch(() => {});
      return next;
    });
  };

  const navItems = [
    ...baseNav,
    ...(canSeeDollarStats(user)
      ? [{ to: "/stats", label: "Stats ($)", icon: BarChart3, testId: "nav-stats" }]
      : []),
    ...(canManageUsers(user)
      ? [{ to: "/admin/users", label: "Users", icon: Users, testId: "nav-users" }]
      : []),
    ...(canManageNotificationTemplates(user)
      ? [{ to: "/admin/notification-templates", label: "Notifications", icon: Bell, testId: "nav-notification-templates" }]
      : []),
  ];

  return (
    <div className="h-screen bg-[#050505] text-zinc-100 flex overflow-hidden">
      <aside className={`${collapsed ? "w-14" : "w-48"} shrink-0 border-r border-zinc-900 flex flex-col bg-[#080808] transition-[width] duration-200`} data-testid="sidebar" data-collapsed={collapsed ? "true" : "false"}>
        <div className={`${collapsed ? "px-2 py-4 items-center" : "px-5 py-5"} border-b border-zinc-900 flex flex-col gap-0.5`}>
          <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}>
            <TrendingUp className="w-8 h-8 text-blue-500 shrink-0" />
            {!collapsed && <span className="font-heading font-semibold text-2xl tracking-tight" data-testid="app-logo">ZigZagX</span>}
          </div>
          {!collapsed && (
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-mono pl-10">Trade with confluence</span>
          )}
        </div>
        {canCreateTrade(user) && (
          <div className={`${collapsed ? "px-2" : "px-3"} py-4`}>
            {collapsed ? (
              <button
                onClick={() => setSheetOpen(true)}
                data-testid="new-trade-btn"
                title="New Trade"
                className="w-full h-9 flex items-center justify-center rounded-sm bg-blue-600 hover:bg-blue-500 text-white"
              >
                <Plus className="w-4 h-4" />
              </button>
            ) : (
              <Button onClick={() => setSheetOpen(true)} data-testid="new-trade-btn" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-white h-9 gap-2">
                <Plus className="w-4 h-4" /> New Trade
              </Button>
            )}
          </div>
        )}
        <nav className={`flex-1 ${collapsed ? "px-1" : "px-2"} ${canCreateTrade(user) ? "" : "pt-4"}`}>
          {navItems.map((item) => {
            const active = pathname === item.to || (item.to !== "/journal" && pathname.startsWith(item.to));
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                title={collapsed ? item.label : undefined}
                className={`flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2 rounded-sm text-sm mb-0.5 transition-colors ${
                  active
                    ? collapsed
                      ? "bg-zinc-900 text-zinc-100"
                      : "bg-zinc-900 text-zinc-100 border-l-2 border-blue-500 pl-[10px]"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50"
                }`}
              >
                <Icon className={`w-4 h-4 ${active && collapsed ? "text-blue-400" : ""}`} />
                {!collapsed && item.label}
              </Link>
            );
          })}
        </nav>
        <div className={`border-t border-zinc-900 ${collapsed ? "p-2" : "p-3"}`}>
          {!collapsed && (
            <>
              <div className="text-xs text-zinc-500 px-2 font-mono truncate" data-testid="user-email">{user?.email}</div>
              <div className={`text-[10px] uppercase font-mono tracking-wider px-2 pb-2 ${ROLE_TONE[user?.role] || "text-zinc-500"}`} data-testid="user-role">
                {ROLE_LABELS[user?.role] || "General User"}
              </div>
            </>
          )}
          <button
            onClick={toggleCollapsed}
            data-testid="sidebar-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2 px-2"} py-1.5 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/50 rounded-sm mb-1`}
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <><ChevronsLeft className="w-4 h-4" /> Collapse</>}
          </button>
          <Link
            to="/help"
            data-testid="nav-help"
            title={collapsed ? "Help" : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2 px-2"} py-1.5 text-sm rounded-sm mb-1 transition-colors ${
              pathname.startsWith("/help")
                ? "text-blue-400 bg-blue-500/10"
                : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/50"
            }`}
          >
            <HelpCircle className="w-4 h-4" /> {!collapsed && "Help"}
          </Link>
          <button
            onClick={async () => { await logout(); nav_go("/login"); }}
            data-testid="logout-btn"
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2 px-2"} py-1.5 text-sm text-zinc-400 hover:text-rose-400 hover:bg-zinc-900/50 rounded-sm`}
          >
            <LogOut className="w-4 h-4" /> {!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>

      <NewTradeSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onCreated={() => {
          if (pathname !== "/journal") nav_go("/journal");
        }}
      />
    </div>
  );
}
